import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { AuthService } from '../lib/auth';
import { AuthStatus } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Shield, AlertCircle } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setLocation] = useLocation();
  const authService = AuthService.getInstance();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const status = await authService.getAuthStatus();
        setAuthStatus(status);

        // 認証が必要で未認証の場合はログインページへ
        if (status.authRequired && !status.isAuthenticated) {
          setLocation('/login');
          return;
        }
      } catch (error) {
        console.error('認証チェックエラー:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authService, setLocation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medical-light">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-medical-blue mb-4" />
            <p className="text-gray-600">認証状態を確認中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 認証が不要、または認証済みの場合は子コンポーネントを表示
  if (!authStatus?.authRequired || authStatus.isAuthenticated) {
    return <>{children}</>;
  }

  // 認証が必要で未認証の場合はログインページへのリダイレクト処理
  return (
    <div className="min-h-screen flex items-center justify-center bg-medical-light">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl">認証が必要です</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-gray-600 mb-6">
            このサービスを利用するには認証が必要です。
          </p>
          <Button 
            onClick={() => setLocation('/login')} 
            className="w-full bg-medical-blue hover:bg-blue-600"
          >
            <Shield className="mr-2 h-4 w-4" />
            ログインページへ
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
